<template>
  <div>
    <CRow class="d-flex align-items-center justify-start my-2 py-1">
      <CCol style="min-height: 50px">
        <a @click="redirectBack" class="text-decoration-none d-flex-inline align-items-center font-weight-normal text-gray-600 cursor-pointer">
          <CIcon name="arrow" :height="20" class="icon__rotateLeft mr-2 text-uppercase" viewBox="0 0 32 32" />
          <span>{{ $t('unlayer.emailEditor.backButton') }}</span>
        </a>
        <div v-show="isLoadingAutoSave" class="ml-4" style="display: inline-block">
          <img style="width: 30px" src="img/Gif_autosave.gif" />
          <span class="ml-2 text-gray-600">{{ $t('unlayer.emailEditor.save.saving') }}</span>
        </div>
        <transition name="fade">
          <MenssageAlert class="w-75 ml-3" :showProgress="false" :messages="messages" style="display: inline-block" />
        </transition>
      </CCol>
      <CCol class="text-center">
        <CInput
          ref="templateNameInput"
          :placeholder="$t('unlayer.emailEditor.name')"
          class="d-flex justify-content-center mb-0 w-100"
          v-model="$v.templateNameInput.$model"
          :isValid="checkIfValid('templateNameInput')"
          @input="handleDelayValidation"
          :disabled="isLoadingCondition"
        />
        <div class="text-danger" v-if="$v.templateNameInput.$error && $v.templateNameInput.$invalid">
          {{ $t('unlayer.emailEditor.invalid.name') }}
        </div>
      </CCol>
      <CCol class="text-right">
        <CButton v-if="exportable" color="secondary" @click="exportEmitter" class="px-3 py-2 ml-2">
          <span>Export</span>
        </CButton>
        <CButton :disabled="isMainBtnDisabled || isLoadingCondition" color="primary" @click="emitMainAction()" class="px-3 py-2 ml-2">
          <span>{{ mainBtnValue }}</span>
          <CSpinner v-if="isLoadingMainAction" color="primary" class="ml-3" style="width: 1.5rem; height: 1.5rem" />
        </CButton>
      </CCol>
    </CRow>
    <p v-if="!templateId" class="text-center">{{ $t('unlayer.emailEditor.template.info') }}</p>
    <CRow v-if="templateId" class="mb-3" style="min-height: 30px">
      <CCol>
        <div>
          <input class="w-50" @change="importTemplateJson" type="file" id="templateJsonImport" :disabled="isLoadingCondition" />
          <p v-if="invalidTemplateJson" class="text-danger">{{ $t('unlayer.emailEditor.invalid.json') }}</p>
        </div>
        <!-- <div v-show="isLoadingAutoSave" class="mt-2">
          <img style="width: 30px" src="img/Gif_autosave.gif" />
          <span class="ml-2 text-gray-600">{{ $t('unlayer.emailEditor.save.saving') }}</span>
        </div> -->
      </CCol>
      <CCol class="d-flex items-center flex-row justify-content-end align-items-center">
        {{ $t('unlayer.emailEditor.theme.1') }}
        <CSwitch
          :disabled="isMainBtnDisabled || isLoadingCondition"
          @update:checked="changeTheme"
          class="mx-2"
          color="primary"
          variant="3d"
          shape="pill"
        />
        {{ $t('unlayer.emailEditor.theme.2') }}
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, minLength } from 'vuelidate/lib/validators'
import { readFileAsText } from '@/utils/fileReader'
import MenssageAlert from '@/components/MenssageAlert'

export default {
  name: 'UnlayerHeaderEditor',
  mixins: [validationMixin],
  validations: {
    templateNameInput: {
      required,
      minLength: minLength(3),
    },
  },
  components: {
    MenssageAlert,
  },
  props: {
    isLoadingAutoSave: {
      type: Boolean,
      default: false,
    },
    isLoadingMainAction: {
      type: Boolean,
      default: false,
    },
    isLoadingTemplateData: {
      type: Boolean,
      default: false,
    },
    templateId: {
      type: String,
      default: '',
    },
    templateName: {
      type: String,
      default: '',
    },
    messages: {
      type: Array,
      default: [],
    },
    exportable: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      showDiscardModal: false,
      templateNameInput: null,
      invalidTemplateJson: false,
    }
  },
  computed: {
    mainBtnValue() {
      return this.templateId ? this.$t('unlayer.emailEditor.action.primary.save') : this.$t('unlayer.emailEditor.action.primary.create')
    },
    themeControl() {
      return this.$store.state.htmlBuilder.themeControl
    },
    isMainBtnDisabled() {
      return this.$v.$invalid
    },
    isLoadingCondition() {
      return this.isLoadingTemplateData || this.isLoadingMainAction || this.isLoadingAutoSave
    },
  },
  methods: {
    exportEmitter() {
      this.$emit('exportHtml')
    },
    changeTheme(switchValue) {
      const appearanceTheme = switchValue ? 'dark' : 'light'
      this.$store.commit('htmlBuilder/changeAppearanceTheme', appearanceTheme)

      this.$store.commit('htmlBuilder/toggleThemeControl')
      // Truque para lidar com o re-render do componente Unlayer
      this.$nextTick(() => {
        this.$store.commit('htmlBuilder/toggleThemeControl')
        this.$emit('themeChanged')
      })
    },
    redirectBack() {
      this.$router.push({ name: 'new-template-list' })
    },
    setShowDiscardModal() {
      this.showDiscardModal = true
    },
    checkIfValid(fieldName) {
      const field = this.$v[fieldName]
      if (!field.$dirty) {
        return null
      }
      return !(field.$invalid || null)
    },
    handleDelayValidation() {
      this.$v.$reset()
      setTimeout(() => {
        this.$v.$touch()
      }, 1000)
    },
    async emitMainAction() {
      this.$emit('mainActionClicked', this.templateNameInput)
    },
    clearImportFile() {
      const fileInput = document.getElementById('templateJsonImport')
      if (!fileInput) return
      fileInput.value = null
    },
    async importTemplateJson(templateFile) {
      try {
        const templateText = await readFileAsText(templateFile.target.files[0])
        const templateJson = JSON.parse(templateText)
        this.$emit('templateJsonImported', templateJson)
        this.invalidTemplateJson = false
      } catch (error) {
        this.invalidTemplateJson = true
        this.clearImportFile()
      } finally {
      }
    },
  },
  watch: {
    templateName(newValue) {
      this.templateNameInput = newValue
    },
    isLoadingCondition() {
      this.clearImportFile()
    },
  },
}
</script>
